import { useState, useEffect, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { apiRequest } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';

interface Tiles {
  pendingPayments?: number;
  approvedPayments?: number;
  completedPayments?: number;
  highValuePayments?: number;
  pendingAuthorizations?: number;
  paymentAging?: Record<string, number>;
  vendorSummary?: number;
}

/** L15.01 — Payment Dashboard: KPI overview of the payment lifecycle (§7). */
export function PaymentDashboardPage(): ReactNode {
  const { t } = useTranslation();
  const { token } = useAuth();
  const [tiles, setTiles] = useState<Tiles | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    apiRequest<Tiles>('/v1/payments/dashboard/tiles', { token })
      .then(setTiles)
      .catch(e => setError((e as Error).message))
      .finally(() => setLoading(false));
  }, [token]);

  const tile = (label: string, value: ReactNode, color: string) => (
    <div style={{ flex: '1 1 160px', minWidth: 160, padding: 16, background: 'var(--color-bg-surface)', border: `1px solid var(--color-border-default)`, borderTop: `3px solid ${color}`, borderRadius: 8 }}>
      <div style={{ fontSize: 28, fontWeight: 700 }}>{value ?? 0}</div>
      <div style={{ color: 'var(--color-text-muted)', fontSize: 13 }}>{label}</div>
    </div>
  );

  return (
    <div style={{ padding: 24 }}>
      <h1>{t('finance.dash.title')}</h1>
      {error && <p style={{ color: 'red' }} role="alert">{error}</p>}
      {loading && <p>{t('finance.dash.loading')}</p>}
      {tiles && (
        <>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, marginBottom: 24 }}>
            {tile(t('finance.dash.pending'), tiles.pendingPayments, 'var(--color-text-link)')}
            {tile(t('finance.dash.approved'), tiles.approvedPayments, 'var(--color-success)')}
            {tile(t('finance.dash.completed'), tiles.completedPayments, 'var(--color-success)')}
            {tile(t('finance.dash.highValue'), tiles.highValuePayments, 'var(--color-accent-purple)')}
            {tile(t('finance.dash.pendingAuth'), tiles.pendingAuthorizations, 'var(--color-accent-pink)')}
            {tile(t('finance.dash.vendors'), tiles.vendorSummary, 'var(--color-text-muted)')}
          </div>
          {tiles.paymentAging && (
            <div style={{ marginBottom: 24 }}>
              <h2 style={{ fontSize: 16 }}>{t('finance.dash.aging')}</h2>
              <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                {Object.entries(tiles.paymentAging).map(([bucket, count]) => (
                  <div key={bucket} style={{ padding: 12, background: 'var(--color-bg-muted)', borderRadius: 6, minWidth: 100 }}>
                    <div style={{ fontWeight: 700 }}>{count}</div>
                    <div style={{ color: 'var(--color-text-muted)', fontSize: 12 }}>{bucket}</div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}
      <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
        <Link to="/finance/maker/queue">{t('finance.dash.linkMaker')}</Link>
        <Link to="/finance/payment-entry">{t('finance.dash.linkPaymentEntry')}</Link>
        <Link to="/finance/reconciliation">{t('finance.dash.linkRecon')}</Link>
        <Link to="/finance/holds">{t('finance.dash.linkHolds')}</Link>
        <Link to="/finance/gst">{t('finance.dash.linkGst')}</Link>
        <Link to="/finance/tracking">{t('finance.dash.linkTracking')}</Link>
        <Link to="/finance/advice">{t('finance.dash.linkAdvice')}</Link>
      </div>
    </div>
  );
}
